<template>
  <div class="page">
    <div class="page-header-container">
      <div class="container">
        <div class="page-header">
          <div class="title-container">
            <div class="title">ความคิดเห็น</div>
          </div>
          <router-link
            to="/comments/setting"
            class="btn btn-primary ml-auto">
            ตั้งค่าอีเมลผู้รับความคิดเห็น
          </router-link>
        </div>
      </div>
    </div>
    <div class="page-body">
      <div class="container">
        <PreloaderSection v-if="loadCommentState.isLoading" />
        <div v-if="!loadCommentState.isLoading">
          <Section v-if="!loadCommentState.error">
            <template v-slot:section-filter>
              <input
                class="form-control form-control-search"
                placeholder="ค้นหาด้วยชื่อผู้เขียน..."
                :value="filter.q"
                @change="onSearch"/>
              <Dropdown :alignment="'right'" class="search-dropdown ml-2">
                <template v-slot:dropdow-button>
                  <button type="button" class="btn btn-action m-0">
                    <i class="fa fa-chevron-down" />
                  </button>
                </template>
                <div class="dropdown-category">
                  <div class="name">หมวดหมู่</div>
                  <select
                    class="form-control long-choice"
                    @change="onSelectedStatus">
                    <option value="any">ทั้งหมด</option>
                    <option
                      v-for="item in commentCategories"
                      :key="item.id"
                      :value="item.id">
                      {{ item.name }}
                    </option>
                  </select>
                </div>
              </Dropdown>
            </template>
            <template v-slot:section-body>
              <div v-if="!isEmpty()">
                <List>
                  <template v-slot:list-header>
                    <div class="list-item-detail">
                      <div class="list-item list-item-primary list-item-title">
                        ชื่อผู้ส่ง
                      </div>
                      <div class="list-item">ส่งมื่อ</div>
                      <div class="list-item list-item-action"></div>
                    </div>
                  </template>
                  <template v-slot:list-body>
                    <div
                      class="list-item-container no-underline"
                      v-for="(item, itemIndex) in comments"
                      :key="item.id"
                      @click.stop="goToPageDetail(`/comments/${item.id}`, itemIndex, $event)">
                      <div class="list-item-detail">
                        <div class="list-item list-item-primary list-item-title">
                          <div class="information-container">
                            <div class="category"
                              :class="showClassCommentCategory(item.contactFormCategoryId)"
                              v-if="!loadCommentCategoryState.isLoading">
                              {{ showCommentCategoryTitle(item) }}
                              {{getCommentCategoryName('contactFormCategory', item, 'name')}}
                            </div>
                            <div class="full-name">
                              จากคุณ {{ item.fullName }}
                            </div>
                          </div>
                          <div class="comment-container">
                            <div class="comment subtitle">
                              {{ item.comment }}
                            </div>
                          </div>
                        </div>
                        <div class="list-item">
                          {{ formatDateTime(item.submittedAt) }}
                        </div>
                        <div class="list-item list-item-action action">
                          <div class="action-container">
                            <router-link
                              :to="`/comments/${item.id}`"
                              class="btn btn-light btn-action">
                              <i class="fas fa-chevron-right"></i>
                            </router-link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </List>
                <div class="pagination-container" v-if="isShowPagination()">
                  <Pagination :lastPage="lastPage" />
                </div>
              </div>
              <!-- empty list --->
              <EmptyMessage v-if="isEmpty()">
                <h3>ไม่มีความคิดเห็น</h3>
              </EmptyMessage>
              <!-- search result -->
              <EmptyMessage v-if="isEmptySearch()">
                <h3>ไม่พบความคิดเห็นที่ค้นหา</h3>
                <button
                  type="button"
                  class="btn btn-light btn-lg mt-4"
                  @click="clearSearch">
                  ล้างการค้นหา
                </button>
              </EmptyMessage>
            </template>
          </Section>
          <!-- error from api -->
          <ErrorMessageCommentContainer
            v-if="loadCommentState.error"
            :error="loadCommentState.error"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import dateFormetter from '@/utils/dateFormatter';
import Section from '@/components/Section.vue';
import List from '@/components/List.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';
import Pagination from '@/components/Pagination.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import Dropdown from '@/components/Dropdown.vue';

import { loadComments, loadCommentCategory } from './api';
import ErrorMessageCommentContainer from './ErrorMessageCommentContainer.vue';

export default {
  mixins: [dateFormetter],
  components: {
    Section,
    List,
    PreloaderSection,
    Pagination,
    EmptyMessage,
    Dropdown,
    ErrorMessageCommentContainer,
  },
  data() {
    return {
      comments: null,
      lastPage: null,
      loadCommentState: {
        isLoading: true,
        error: null,
      },
      loadCommentCategoryState: {
        isLoading: true,
        error: null,
      },
      commentCategories: null,
      isSearch: null,
      deleteScheduleState: {
        isDeleting: false,
        error: null,
      },
      selectedStatus: 'all',
    };
  },
  computed: {
    filter() {
      return this.$route.query;
    },
  },
  watch: {
    filter(newValue) {
      this.load(newValue);
    },
  },
  created() {
    this.load(this.filter);
    this.loadCategory();
  },
  methods: {
    async load(filter) {
      try {
        const response = await loadComments(filter);
        this.comments = response.data.data;
        this.lastPage = response.data.lastPage;
        this.loadCommentState.isLoading = false;
      } catch (error) {
        this.loadCommentState.error = error.response;
        this.loadCommentState.isLoading = false;
      }
    },
    async loadCategory() {
      try {
        const response = await loadCommentCategory();
        this.commentCategories = response.data.data;
        this.loadCommentCategoryState = false;
      } catch (error) {
        this.loadCommentCategoryState.error = error.response;
        this.loadCommentCategoryState.isLoading = false;
      }
    },
    getCommentCategoryName(type, commentItem, fieldName) {
      return _.get(commentItem[type], fieldName, 'ความคิดเห็น');
    },
    getCommentProgramName(type, commentItem, path) {
      return _.get(commentItem[type], path, 'ไม่ระบุ');
    },
    getCommentProgramCategory(type, commentItem, path) {
      return _.get(commentItem[type], path, 'ไม่ระบุ');
    },
    isEmptySearch() {
      return _.isEmpty(this.comments) && !_.every(this.filter, _.isEmpty);
    },
    isEmpty() {
      return (
        _.isEmpty(this.comments) && !this.isSearch
        && _.every(this.filter, _.isEmpty)
      );
    },
    onSearch(event) {
      let filter;
      if (event.target.value) {
        filter = { ...this.filter, q: event.target.value };
      } else {
        filter = { ...this.filter };
        delete filter.q;
      }
      this.isSearch = true;
      this.$router.push({ query: { ...filter } });
    },
    clearSearch() {
      this.$router.push({ query: null });
    },
    isShowPagination() {
      return this.lastPage > 1;
    },
    onSelectedStatus(event) {
      const contactFormCategoryId = event.target.value;
      if (contactFormCategoryId !== 'any') {
        const filter = { ...this.filter, contactFormCategoryId };
        this.$router.push({ query: { ...filter } });
      } else {
        this.$router.push('comments');
      }
    },
    showClassCommentCategory(itemId) {
      const category = _.find(this.commentCategories, { id: itemId });
      if (!category) return;
      if (category.key === 'report') return 'danger';
      if (category.key === 'asking') return 'success';
      if (category.key === 'suggestion') return 'warning';
    },
    isContentCategoryShowing(commentItem) {
      const program = _.get(commentItem, 'program', null);
      return !!program;
    },
    showCommentCategoryTitle(item) {
      const category = this.getCommentCategoryName('contactFormCategory', item, 'name');
      if (category === 'อื่นๆ') return 'ความคิดเห็น';
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/layouts/page.scss';
@import './src/assets/scss/themes/components/form.scss';

.list-item-primary {
  overflow: hidden;
}

.category-name {
  padding-left: $spacer * 0.5 !important;
}

.action {
  justify-content: center !important;
}

.title {
  font-weight: bold;
  color: $gray-700;
}

.warning {
  color: darken($warning, 7%);
}
.danger {
  color: darken($danger, 7%);
}
.success {
  color: darken($success, 7%);
}

.primary,
.secondary {
  color: $card-text;
}

.dropdown-category {
  padding: $spacer * 0.25 $spacer;

  .name {
    margin-bottom: $spacer * 0.25;
  }

  .long-choice {
    width: initial;
  }
}

.information-container {
  display: flex;
  max-width: 100%;
  .category {
    margin-right: $spacer / 4;
  }
  .full-name {
    max-width: 80%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.comment-container {
  display: flex;
  max-width: 100%;
  overflow: hidden;
  .comment {
    // max-width: 80%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

@media (max-width: 991.98px) {
  .information-container {
    display: block;
    .category {
      font-size: $font-size-base;
    }
    .full-name {
      max-width: 100%;
    }
  }
  .comment-title {
    display: block;
  }
}

// @media (min-width: 576px) and (max-width: 1023.98px) {
//   .toggle {
//     margin-right: 0px;
//   }

//   .list-item-container {
//     .list-item-image {
//     }

//     .list-item-detail {
//       .list-item {
//         &.list-item-status {
//           // display: none;
//         }
//         &.list-item-date {
//           display: none;
//         }
//         &.list-item-action {
//           .action-container {
//             .btn-external {
//               display: none;
//             }
//           }
//         }
//       }
//     }
//   }
// }
</style>
